import React from 'react';
import RsTableHeaderSortControl from '@Pages/RetroSynthesis/components/RsTable/RsTableHeaderSortControl';
import { getAriaSortFromSortList } from '@Pages/RetroSynthesis/components/RsTable/RsTableSortControlUtils';
import TooltipContentRoutes from '@Pages/RetroSynthesis/ProjectRoutes/components/TooltipContentRoutes';
import TooltipContentBlocks from '@Pages/RetroSynthesis/ProjectRoutes/components/TooltipContentBlocks';
import TooltipContentConfidence from '@Pages/RetroSynthesis/ProjectRoutes/components/TooltipContentConfidence';
import StructureToBase64 from '@Containers/StructureDialog/StructureToBase64';
import RsTableHeaderLabel from '@Pages/RetroSynthesis/components/RsTable/RsTableHeaderLabel';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import HoverTooltip from '@Components/HoverTooltip';
import { Flyout } from '@els/els-react--flyout';
import { ButtonWithIcon, Button } from '@els/els-react--button';
import noop from 'lodash/noop';
import TooltipContentFD from './TooltipContentFD';

import './ProjectRoutesTableHeader.scss';

function ProjectRoutesTableHeader({
  sort,
  projectStructure,
  onSortChange,
  onStructureClick = () => null,
  isConfidenceColumnShown,
  isFirstDisconnectionColumnShown,
}) {
  return (
    <tr className="project-routes-table-header c-els-table__row c-els-table__row--head c-els-table__row--sticky">
      <th className="c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">
        <RsTableHeaderLabel />
      </th>
      <th
        className="c-els-table__cell c-els-table__basic-cell c-els-table__cell--header  routes-th"
        aria-sort={getAriaSortFromSortList(sort, 'routeNo')}
        aria-label="Route"
        aria-description="This column shows the routes that were generated ">
        <RsTableHeaderSortControl sortList={sort} sortParam="routeNo" onChange={onSortChange}>
          Routes
        </RsTableHeaderSortControl>
      </th>
      <th
        className="c-els-table__cell c-els-table__basic-cell c-els-table__cell--header"
        aria-label="Building blocks to target"
        aria-describedby="building-block-sr">
        <div className="project-routes-table-header__blocks">
          <div className="project-routes-table-header__blocks--content">
            <HoverTooltip
              id="tooltip-building-blocks"
              tooltip={ReactDOMServer.renderToString(<TooltipContentBlocks />)}
              theme="simple"
              trigger="click">
              <ButtonWithIcon
                data-e2e="project-routes-table-building-blocks-tooltip-button"
                className="u-els-margin-right-1o2"
                sprite="InformationOutlineCircle"
                iconOnly
                type="link"
                linkIconSize="xs"
                onClick={noop}>
                Building blocks information
              </ButtonWithIcon>
            </HoverTooltip>
            Building blocks to target
            <StructureToBase64
              structure={projectStructure}
              /* eslint-disable-next-line react/no-unstable-nested-components */
              customRenderer={({ src }) => (
                <Button
                  data-e2e="project-routes-table-target-thumbnail-button"
                  id="project-routes-table-header-target-thumbnail-button"
                  type="borderless"
                  onClick={() => onStructureClick(projectStructure)}
                  className="project-routes-table-header__target-thumbnail-button u-els-padding-top-none u-els-padding-bottom-none e2e-target-thumbnail"
                  aria-label="Target thumbnail">
                  <img
                    src={src}
                    alt="Building blocks to target structure"
                    className="project-routes-table-header__target-thumbnail-image"
                  />
                </Button>
              )}
            />
          </div>

          <RsTableHeaderSortControl sortList={sort} sortParam="stepCount" onChange={onSortChange}>
            Number of steps
          </RsTableHeaderSortControl>
        </div>
      </th>
      {isFirstDisconnectionColumnShown && (
        <th
          className="c-els-table__cell c-els-table__basic-cell c-els-table__cell--header first-disconnect-th"
          aria-label="Route topology"
          aria-description="This column shows the Route topology thumbnail, providing a quick overview of the complexity of the found route.">
          <div className="project-routes-table-header__item">
            <HoverTooltip id="tooltip-fd" trigger="click" tooltip={ReactDOMServer.renderToString(<TooltipContentFD />)} theme="simple">
              <ButtonWithIcon
                data-e2e="project-routes-table-first-disconnection-tooltip-button"
                className="u-els-margin-right-1o2"
                sprite="InformationOutlineCircle"
                iconOnly
                type="link"
                linkIconSize="xs"
                onClick={noop}>
                First disconnection information
              </ButtonWithIcon>
            </HoverTooltip>
            First disconnection
          </div>
        </th>
      )}
      <th className="table__cell c-els-table__cell c-els-table__basic-cell c-els-table__cell--header" aria-describedby="route-topology-sr">
        <div className="project-routes-table-header__item">
          <HoverTooltip
            id="project-routes-table-header"
            trigger="click"
            tooltip={ReactDOMServer.renderToString(<TooltipContentRoutes />)}
            theme="simple">
            <ButtonWithIcon
              data-e2e="project-routes-table-route-topology-tooltip-button"
              className="u-els-margin-right-1o2"
              sprite="InformationOutlineCircle"
              iconOnly
              type="link"
              linkIconSize="xs"
              onClick={noop}>
              Route topology information
            </ButtonWithIcon>
          </HoverTooltip>
          Route topology
        </div>
      </th>
      {isConfidenceColumnShown && (
        <th
          className="c-els-table__cell c-els-table__basic-cell c-els-table__cell--header score-th"
          aria-sort={getAriaSortFromSortList(sort, 'confidence')}>
          <div className="project-routes-table-header__item">
            <Flyout
              id="tooltip-confidence"
              placement="bottom-start"
              maxHeight="30rem"
              trigger="click"
              flyout={<TooltipContentConfidence />}
              theme="simple"
              shouldCloseOnESC
              pointerType="primary">
              <ButtonWithIcon
                data-e2e="project-routes-table-confidence-tooltip-button"
                className="u-els-margin-right-1o2"
                sprite="InformationOutlineCircle"
                iconOnly
                type="link"
                linkIconSize="xs"
                onClick={noop}>
                Score information
              </ButtonWithIcon>
            </Flyout>
            <RsTableHeaderSortControl sortList={sort} sortParam="confidence" onChange={onSortChange}>
              Score
            </RsTableHeaderSortControl>
          </div>
        </th>
      )}
      <th className="c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">Get to routes</th>
    </tr>
  );
}
ProjectRoutesTableHeader.propTypes = {
  sort: PropTypes.array,
  projectStructure: PropTypes.string,
  onSortChange: PropTypes.func,
  onStructureClick: PropTypes.func,
  isConfidenceColumnShown: PropTypes.bool,
  isFirstDisconnectionColumnShown: PropTypes.bool,
};

export default ProjectRoutesTableHeader;
